import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AgentWithUnreadCount } from '@marageti/z4-sdk/lib/travel';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import useUnreadCount from '../../../hooks/use-unread-count';
import useURLSearchQuery from '../../../hooks/use-url-for-search-query';
import { useAuthStore } from '../../../store';
import MessageInboxView from './messages-inbox-view';
import useInboxConversations from './use-inbox-conversations';

const UNREAD_PARAM = 'unread';

type InboxSearchQuery = { unread: string };

const MessageInboxContainer = () => {
  const navigate = useNavigate();
  const { personId: personIdParam, conversationId: conversationIdParam } = useParams<{ personId: string; conversationId?: string }>();
  const { loggedInAgent } = useAuthStore();
  const [queryValues, updateURLQuery] = useURLSearchQuery<InboxSearchQuery>({ unread: '' });
  const showUnreadOnly = queryValues.unread === 'true';

  const personId = personIdParam ?? loggedInAgent?.id ?? undefined;
  const agentPersonId = personId;
  const selectedAgentPersonId = personIdParam && personIdParam !== loggedInAgent?.id ? personIdParam : null;
  const selectedInboxConversationId = conversationIdParam;

  const {
    data: partnerAgentsWithUnread,
    unreadCount,
  } = useUnreadCount(agentPersonId);

  const buildInboxPath = useCallback((pathPersonId: string, conversationId?: string, unreadOnly?: boolean) => {
    const path = conversationId ? `/inbox/${pathPersonId}/${conversationId}` : `/inbox/${pathPersonId}`;
    const search = unreadOnly ? `?${UNREAD_PARAM}=true` : '';
    return `${path}${search}`;
  }, []);

  const handleAgentSelect = useCallback((agent: AgentWithUnreadCount | null) => {
    const newPersonId = agent?.id ?? loggedInAgent?.id ?? '';
    navigate(buildInboxPath(newPersonId, undefined, showUnreadOnly));
  }, [loggedInAgent?.id, navigate, buildInboxPath, showUnreadOnly]);

  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(true);
  const toggleLeftSidebar = useCallback(() => setIsLeftSidebarOpen((prev) => !prev), []);
  const onClickUnread = useCallback(() => updateURLQuery({ unread: showUnreadOnly ? '' : 'true' }), [showUnreadOnly, updateURLQuery]);
  const isAgentAdmin = loggedInAgent?.userInfo?.some((userInfo) => userInfo?.roles?.includes(RoleName.AGENT_ADMIN)) ?? false;

  const {
    conversationsMap,
    hasMoreConversations,
    isInitialLoad,
    isLoading,
    isValidating,
    handleLoadMoreConversations,
    markConversationRead,
    markConversationUnread,
    optimisticMarkConversationAsRead,
  } = useInboxConversations(agentPersonId, showUnreadOnly);

  const onSelectConversation = useCallback((conversationId: string) => {
    if (selectedInboxConversationId === conversationId) {
      navigate(buildInboxPath(personId ?? '', undefined, showUnreadOnly));
      return;
    }
    navigate(buildInboxPath(personId ?? '', conversationId, showUnreadOnly));
    if (selectedAgentPersonId === null) {
      optimisticMarkConversationAsRead(conversationId);
    }
  }, [
    navigate,
    buildInboxPath,
    personId,
    showUnreadOnly,
    selectedInboxConversationId,
    selectedAgentPersonId,
    optimisticMarkConversationAsRead,
  ]);

  return (
    <MessageInboxView
      conversationsMap={conversationsMap}
      hasConversations={Object.keys(conversationsMap).length > 0}
      hasMoreConversations={hasMoreConversations}
      isInitialLoad={isInitialLoad}
      isLeftSidebarOpen={isLeftSidebarOpen}
      isLoading={isLoading}
      isValidating={isValidating}
      loggedInAgentId={loggedInAgent?.id}
      isAgentAdmin={isAgentAdmin}
      onAgentSelect={handleAgentSelect}
      markConversationRead={markConversationRead}
      markConversationUnread={markConversationUnread}
      onLoadMoreConversations={handleLoadMoreConversations}
      onSelectConversation={onSelectConversation}
      onClickUnread={onClickUnread}
      partnerAgentsWithUnread={partnerAgentsWithUnread ?? undefined}
      selectedAgentPersonId={selectedAgentPersonId}
      selectedInboxConversationId={selectedInboxConversationId}
      showUnreadOnly={showUnreadOnly}
      toggleLeftSidebar={toggleLeftSidebar}
      unreadCount={unreadCount}
      selectedAgentId={personId ?? ''}
    />
  );
};

export default MessageInboxContainer;
